import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

import {
  containerStyle,
  legendContainerStyle,
  legendItemStyle,
  legendBoxStyle,
  legendTextStyle,
  titleStyle,
  chartWrapperStyle,
} from "./styles";
import { Message } from "@/types";

interface MessageChartsProps {
  message: Message;
}

export const MessageCharts = ({ message }: MessageChartsProps) => {
  if (!message.chart_name || !message.items || message.items.length !== 2) {
    return null;
  }

  const chartData = message.items;

  const getFillColor = (name: string) => {
    if (name.toLowerCase().includes("before")) {
      return "url(#beforeGradient)";
    } else if (name.toLowerCase().includes("after")) {
      return "url(#afterGradient)";
    }
    return "#000";
  };

  const minValue = 0;
  const maxValue = Math.max(5, ...chartData.map((item) => item.value));
  const roundedMax = Math.ceil(maxValue);

  const generateTicks = () => {
    const ticks = [];
    for (let i = 0; i <= roundedMax; i += 0.5) {
      ticks.push(i);
    }
    return ticks;
  };

  const compactChartWrapperStyle = {
    ...chartWrapperStyle,
    height: "120px",
    maxWidth: "100%",
    mx: "auto",
  };

  return (
    <Box sx={containerStyle}>
      <Typography sx={titleStyle}>{message.chart_name}</Typography>

      <Box
        sx={{
          ...legendContainerStyle,
          mt: 0.5,
          mb: 0.5,
        }}
      >
        <Box sx={{ ...legendItemStyle, ml: "5px" }}>
          <Box
            sx={{
              ...legendBoxStyle(
                "linear-gradient(90deg, #9056E9 0%, #C39EFF 100%)",
              ),
              width: "12px",
              height: "12px",
            }}
          />
          <Typography
            sx={{
              ...legendTextStyle,
              fontSize: "0.75rem",
            }}
          >
            Before Lesson
          </Typography>
        </Box>
        <Box sx={legendItemStyle}>
          <Box
            sx={{
              ...legendBoxStyle(
                "linear-gradient(90deg, #66EF64 0%, #66EF6433 100%)",
              ),
              width: "12px",
              height: "12px",
            }}
          />
          <Typography
            sx={{
              ...legendTextStyle,
              fontSize: "0.75rem",
            }}
          >
            After Lesson
          </Typography>
        </Box>
      </Box>

      {chartData.length === 0 ? (
        <Typography
          sx={{
            fontSize: 12,
            color: "#9CA3AF",
            textAlign: "center",
            mt: "20px",
          }}
        >
          No data to display
        </Typography>
      ) : (
        <Box sx={compactChartWrapperStyle}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 15, bottom: 5, left: 5 }}
            >
              <defs>
                <linearGradient id="beforeGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#C39EFF" />
                  <stop offset="100%" stopColor="#9056E9" />
                </linearGradient>
                <linearGradient id="afterGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#66EF6433" />
                  <stop offset="100%" stopColor="#66EF64" />
                </linearGradient>
              </defs>

              <XAxis
                type="number"
                domain={[0, roundedMax]}
                ticks={generateTicks()}
                tick={{ fill: "#9CA3AF", fontSize: 10 }}
                axisLine={{ stroke: "#9CA3AF33" }}
                tickLine={{ stroke: "#9CA3AF33" }}
                height={20}
              />
              <YAxis dataKey="name" type="category" hide />

              <Tooltip />

              <Bar dataKey="value" barSize={30} radius={[0, 6, 6, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getFillColor(entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};
